import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { FaTiktok } from 'react-icons/fa';
import { fadeUp } from '../utils/animations';
import GlowShape from '../components/GlowShape';
import Section from '../components/Section';
import StatCard from '../components/StatCard';
import BrandShowcase from '../components/BrandShowcase';
import SpeakingEventCard from '../components/SpeakingEventCard';
import TestimonialsSection from '../components/TestimonialsSection';
import HeroSection from '../components/HeroSection';
import SafeIcon from '../common/SafeIcon';

const {
  FiCpu, FiBriefcase, FiMic, FiCast,
  FiStar, FiAward, FiRadio, FiHeart,
  FiPlayCircle, FiTarget, FiArrowRight,
  FiFacebook, FiInstagram, FiLinkedin,
} = FiIcons;

const SPEAKING_EVENTS = [
  { title: "Sex, Money, Business", organization: "Truth Summit - 2024", date: "August 2024", location: "Trenton, NJ", type: "Keynote" },
  { title: "Understanding AI Creative Skills", organization: "Gospel Music Workshop of America", date: "July 2024", location: "Memphis, TN", type: "Workshop" },
  { title: "Avid Speaker Series", organization: "Isaiah Creswell School of the Arts", date: "December 2024", location: "Nashville, TN", type: "Masterclass" },
  { title: "AI & Office 365", organization: "Tennessee State University", date: "July 2024", location: "Nashville, TN", type: "Corporate Training" },
];

const BRANDS = [
  { 
    name: "The JRob United Group", 
    category: "AI Consulting, Training & Application", 
    icon: FiBriefcase, 
    tagline: "Strategic Transformation", 
    longDesc: "The parent brand for AI consulting and leadership development. We help organizations navigate the complexities of digital transformation while maintaining a human-centered approach.", 
    features: ["AI Readiness Assessments", "Executive Coaching", "Workflow Optimization", "Strategic Planning"],
    ctas: [
      { text: "Visit JRob United", url: "https://www.jrobunited.com", primary: true }
    ]
  },
  { 
    name: "The Cultivation Hub", 
    category: "Education & Inspiration", 
    icon: FiCast, 
    tagline: "Empowering Growth", 
    longDesc: "A digital ecosystem dedicated to lifelong learning. Through webinars, courses, and the Cultivation Hub Podcast, we provide the tools needed to thrive in the modern economy.", 
    features: ["Digital Courses", "Premium Webinars", "Resource Library", { text: "The Cultivation Hub Podcast", url: "https://www.youtube.com/@chubOnDemand" }],
    ctas: [
      { text: "Listen to the latest episode", url: "https://www.youtube.com/@chubOnDemand", primary: true }
    ]
  },
  { 
    name: "AI Mavericks", 
    category: "Community", 
    icon: FiCpu, 
    tagline: "Confidence in Innovation", 
    longDesc: "Our flagship learning community for AI beginners. We demystify artificial intelligence and provide practical, hands-on training for people from all walks of life.", 
    features: ["Weekly AI News & Updates", "Tool Tutorials", "Member Forums", "Resource Templates"], 
    ctas: [
      { text: "Join our Facebook Group", url: "https://www.facebook.com/groups/jrobmavericks", primary: true, className: "bg-[#1877F2] text-white hover:bg-[#1877F2]/90" }
    ]
  },
  { 
    name: "JRob Unplugged", 
    category: "Media / Podcast", 
    icon: FiRadio, 
    tagline: "Reflective Dialogue", 
    longDesc: "A sanctuary for deep thought and quiet reflection. This show explores the intersection of faith, humanity, and progress through intimate storytelling and interviews.", 
    features: ["Deep-dive Interviews", "Reflective Essays", "Community Q&A", "Live Audio Rooms"],
    ctas: [
      { text: "Launches Summer 2026", url: null, primary: false }
    ]
  },
  { 
    name: "IMAC Conference", 
    category: "Faith-Based Conferences & Events", 
    icon: FiMic, 
    tagline: "Creative Convergence", 
    longDesc: "A multi-disciplinary conference blending public speaker, arts, leadership, and entrepreneurship. It's where visionaries meet to spark the next generation of impact.", 
    features: ["Main Stage Keynotes", "Breakout Sessions", "Networking Mixers", "Creative Showcases"],
    ctas: [
      { text: "Learn More", url: "https://imac4u.com/", primary: false }
    ]
  },
  { 
    name: "Jet Foundation, Inc.", 
    category: "Non-Profit", 
    icon: FiHeart, 
    tagline: "Community Impact", 
    longDesc: "Our non-profit arm focused on professional development and mentorship. We bridge the gap for underserved communities by providing technology training and leadership resources.", 
    features: ["Youth Mentorship", "Tech Scholarships", "Community Workshops", "Grant Programs"],
    ctas: [
      { text: "Learn More", url: "https://jetfoundationinc.com/", primary: false }
    ]
  },
  { 
    name: "The Strategic Creator", 
    category: "Curator, Content Creator, Visionary", 
    icon: FiTarget, 
    tagline: "Purposeful Content", 
    longDesc: "Helping creators and public figures build sustainable platforms. We focus on content strategy that aligns with personal values and long-term impact goals.", 
    features: ["Content Strategy", "Brand Identity", "Platform Scaling", "Audience Insights"],
    ctas: [
      { text: "More to Come - Stay Tuned!", url: null, primary: false }
    ]
  },
];

const OFFERS = [
  [FiMic, "Speaking", "Keynotes, panels, retreats, and featured conversations designed to inspire and activate."],
  [FiBriefcase, "Business Consulting", "Strategic guidance for organizations, SMBs, and freelancers navigating growth, AI adoption, and transformation."],
  [FiAward, "Coaching & Training", "Practical AI and leadership coaching and training for teams, organizations, and learning communities."],
  [FiStar, "Media & Podcasts", "A trusted guest voice for interviews, podcasts, digital shows, and thought-leadership conversations."],
];

function AboutImage() {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      whileHover={{ scale: 1.02, y: -5 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      viewport={{ once: true, amount: 0.2 }}
      className="relative flex-shrink-0 w-full md:w-72 lg:w-80 xl:w-96 group cursor-pointer"
    >
      {/* Glow ring behind image */}
      <div className="absolute inset-0 rounded-[32px] bg-gradient-to-br from-blue-500/30 via-purple-500/20 to-orange-500/20 blur-2xl scale-105 -z-10 transition-all duration-500 group-hover:scale-110 group-hover:blur-3xl group-hover:opacity-80" />

      {/* Border gradient wrapper */}
      <div className="rounded-[32px] bg-gradient-to-br from-blue-500/40 via-purple-500/30 to-orange-400/30 p-[1.5px] transition-colors duration-500 group-hover:from-blue-400/60 group-hover:via-purple-400/50 group-hover:to-orange-300/50">
        <div className="relative overflow-hidden rounded-[31px] bg-[#0d1528] aspect-[3/4]">
          <img
            src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1774765290611-Jeff%20at%20GMWA%202024.png"
            alt="Dr. Jeff Robinson"
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          {/* Bottom caption overlay */}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#060b19]/90 via-[#060b19]/40 to-transparent px-5 py-5 transition-transform duration-500 translate-y-2 group-hover:translate-y-0 opacity-90 group-hover:opacity-100">
            <p className="text-xs uppercase tracking-[0.22em] text-blue-300/80 transition-colors duration-300 group-hover:text-blue-300">Dr. Jeff Robinson</p>
            <p className="mt-1 text-sm font-medium text-white/90">AI Practitioner • Public Figure • Creator</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function DrJeffLandingPage() {
  return (
    <main className="min-h-screen bg-[#060b19] text-white selection:bg-blue-500/30">
      {/* Global background */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.12),transparent_32%),radial-gradient(circle_at_top_right,rgba(124,58,237,0.16),transparent_28%),radial-gradient(circle_at_bottom_center,rgba(249,115,22,0.12),transparent_28%)]" />
        <GlowShape className="left-[-4rem] top-20" />
        <GlowShape className="right-[-6rem] top-40" size="w-96 h-96" color="from-purple-500/25 via-blue-500/15 to-transparent" />
        <GlowShape className="bottom-10 left-1/3" size="w-80 h-80" color="from-orange-500/20 via-purple-500/10 to-transparent" />
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#060b19]/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10 lg:px-16">
          <div>
            <div className="text-lg font-semibold tracking-wide text-white">Dr. Jeff Robinson</div>
            <div className="text-xs uppercase tracking-[0.28em] text-white/50">AI Practitioner • Public Figure • Creator</div>
          </div>
          <nav className="hidden items-center gap-6 text-sm text-white/70 md:flex">
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <a href="#brands" className="hover:text-white transition-colors">Brands</a>
            <a href="#speaking" className="hover:text-white transition-colors">Speaking</a>
            <a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a>
          </nav>
        </div>
      </header>

      {/* Hero — GSAP animated with Right Side Text */}
      <section 
        className="relative overflow-hidden min-h-screen flex items-center bg-cover bg-center bg-no-repeat -mt-[73px]"
        style={{ backgroundImage: "url('https://lh3.googleusercontent.com/pw/AP1GczNhm24Rr88A3pzZf-cWigwGAsKlk5ct7dZxpjO-XZF6ji9waFHrhzwxpjDy3fzIqijaliqvAQCqd50bJZVS5QVsdok5aFtVL9-nXk7qLxYBFRzyzCUFWks1grDd2NPt4gQB4AWf_4iW2wSEewdOyLfc=w1594-h911-s-no-gm?authuser=0')" }}
      >
        {/* Subtle gradient overlay to ensure text readability on the right side while keeping left side mostly clear */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#060b19]/40 to-[#060b19]/90 pointer-events-none" />
        
        <div className="w-full max-w-7xl mx-auto relative z-10 flex px-6 pt-[73px]">
          <HeroSection />
        </div>
      </section>

      {/* About */}
      <Section id="about" eyebrow="About Dr. Jeff" title="A multidimensional voice for a rapidly changing world" subtitle="With more than three decades in technology and decades of leadership, public speaking, and creative development, Dr. Jeff is known for making complex ideas simple, practical, and deeply human.">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:gap-12">

          {/* Placeholder image — left column */}
          <AboutImage />

          {/* Stat cards — right column */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="flex flex-1 flex-col gap-6"
          >
            <motion.div variants={fadeUp} className="grid gap-6 sm:grid-cols-2">
              <StatCard value="30+" label="Years in IT" />
              <StatCard value="250+" label="Individuals Trained Since 2024" />
              <StatCard value="25+" label="Years of Public Speaking & Creative Leadership" />
              <StatCard value="7" label="Brands in the Ecosystem" />
            </motion.div>

            {/* Short bio blurb */}
            <motion.p
              variants={fadeUp}
              whileHover={{ scale: 1.01, backgroundColor: "rgba(255, 255, 255, 0.08)" }}
              transition={{ duration: 0.3 }}
              className="rounded-[24px] border border-white/10 bg-white/5 p-6 text-base leading-8 text-white/70 backdrop-blur-md cursor-default hover:border-white/20 transition-colors"
            >
              Dr. Jeff Robinson is a technologist, leadership architect, author, public speaker, and creative strategist. He bridges the gap between innovation and humanity — helping people and organizations not just survive the age of AI, but thrive within it with purpose and clarity.
            </motion.p>
          </motion.div>
        </div>
      </Section>

      {/* Feedback Prompt Section - Moved right above Recent Engagements */}
      <Section id="feedback-prompt" eyebrow="Feedback" title="Speaking Engagement Feedback" subtitle="Were you part of a recent audience? We'd love to hear your thoughts on Dr. Jeff's session. Your feedback helps refine future talks and create more impactful experiences.">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="flex justify-center">
          <Link to="/feedback" className="inline-flex items-center justify-center rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-500 hover:scale-[1.02] active:scale-[0.98]">
            Leave Feedback
          </Link>
        </motion.div>
      </Section>

      {/* Speaking */}
      <Section id="speaking" eyebrow="Recent Engagements" title="On the stage, behind the mic, leading the room" subtitle="Dr. Jeff brings clarity to the complex, speaking at some of the most influential gatherings across technology, leadership, and community development.">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SPEAKING_EVENTS.map((event, i) => <SpeakingEventCard key={i} event={event} index={i} />)}
        </div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-12 flex justify-center">
          <button className="flex items-center gap-2 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors">
            <SafeIcon icon={FiPlayCircle} className="h-5 w-5" /> Watch Highlight Reel
          </button>
        </motion.div>
      </Section>

      {/* Brands */}
      <Section id="brands" eyebrow="House of Brands" title="An ecosystem designed to educate, empower, and elevate" subtitle="Each brand serves a distinct audience and purpose while reinforcing one unified mission: helping people grow with clarity, confidence, and purpose.">
        <BrandShowcase brands={BRANDS} />
      </Section>

      {/* Topics */}
      <Section eyebrow="Signature Topics" title="Ideas built for stages, panels, and summits" subtitle="Dr. Jeff's talks connect innovation with humanity, helping audiences move from uncertainty to strategy.">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="grid gap-6 lg:grid-cols-2">
          {[
            ["Making AI Human", "Your organization's roadmap to confident AI adoption."],
            ["Is AI the New COVID?", "Disruption, adaptation, and the organizations that will survive."],
            ["Don't Be a Blockbuster", "The cost of refusing to evolve in a changing world."],
            ["The Hummingbird Effect", "How life cross-pollinates purpose — a blend of science and soul."],
          ].map(([title, desc], i) => (
            <motion.div key={title} custom={i} variants={fadeUp} className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur-md">
              <div className="inline-flex items-center rounded-full bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-orange-500/20 px-3 py-1 text-xs uppercase tracking-[0.24em] text-white/75">Signature Topic</div>
              <h3 className="mt-4 text-2xl font-semibold text-white">{title}</h3>
              <p className="mt-3 text-white/70 leading-7">{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* Offers */}
      <Section eyebrow="What Dr. Jeff Does" title="Bookable offers that create momentum" subtitle="From keynote stages to team training to podcast appearances, every engagement is designed to leave audiences better equipped for what comes next.">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {OFFERS.map(([title, desc], i) => {
            // Re-assigning Icons to fix potential nesting destructuring issues
            const OfferIcons = [FiMic, FiBriefcase, FiAward, FiStar];
            const Icon = OfferIcons[i];
            
            return (
              <motion.div key={title} custom={i} variants={fadeUp} className="rounded-[28px] border border-white/10 bg-gradient-to-b from-white/10 to-white/5 p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-orange-300">
                  <SafeIcon icon={Icon} className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-white">{title}</h3>
                <p className="mt-3 text-sm leading-7 text-white/70">{desc}</p>
              </motion.div>
            )
          })}
        </motion.div>
      </Section>

      {/* Testimonials */}
      <div id="testimonials">
        <TestimonialsSection />
      </div>

      {/* Contact */}
      <Section id="contact" eyebrow="Contact" title="Ready to build something meaningful?" subtitle="Use this page as a launch point for bookings, sponsorship conversations, partnerships, podcast guesting, or media opportunities.">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(135deg,rgba(59,130,246,0.18),rgba(124,58,237,0.14),rgba(249,115,22,0.14))] p-[1px]">
          <div className="grid gap-8 rounded-[31px] bg-[#0b1224]/90 p-8 md:grid-cols-[1fr_0.9fr] md:p-10 backdrop-blur-xl">
            <div>
              <h3 className="text-3xl font-semibold text-white md:text-4xl">Bring Dr. Jeff to your stage, team, or platform.</h3>
              <p className="mt-4 max-w-xl text-lg leading-8 text-white/70">Book Dr. Jeff Robinson for speaking engagements, podcast interviews, coaching, training, and strategic conversations at the intersection of AI, leadership, creativity, and human potential.</p>
              
              {/* Social Media Icons */}
              <div className="mt-10 flex items-center gap-4 flex-wrap">
                <a href="https://www.facebook.com/JRobin65" target="_blank" rel="noopener noreferrer" className="flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-white/70 border border-white/10 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all">
                  <SafeIcon icon={FiFacebook} className="h-5 w-5" />
                </a>
                <a href="https://www.instagram.com/drjeffspeaks" target="_blank" rel="noopener noreferrer" className="flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-white/70 border border-white/10 hover:bg-[#E4405F] hover:text-white hover:border-[#E4405F] transition-all">
                  <SafeIcon icon={FiInstagram} className="h-5 w-5" />
                </a>
                <a href="https://www.tiktok.com/@drjeffspeaks" target="_blank" rel="noopener noreferrer" className="flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-white/70 border border-white/10 hover:bg-white hover:text-black hover:border-white transition-all">
                  <SafeIcon icon={FaTiktok} className="h-5 w-5" />
                </a>
                <a href="https://www.linkedin.com/in/jeffrobinsonedd/" target="_blank" rel="noopener noreferrer" className="flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-white/70 border border-white/10 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all">
                  <SafeIcon icon={FiLinkedin} className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div className="grid gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="text-sm uppercase tracking-[0.2em] text-white/50 mb-3">Website</div>
                <a 
                  href="https://jeffrobinson.my.canva.site/drjeffprofile" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center justify-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/20"
                >
                  Read Full Profile <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
                </a>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="text-sm uppercase tracking-[0.2em] text-white/50">Best Uses</div>
                <div className="mt-2 text-lg text-white">Speaking • Podcast Guesting • Consulting • Coaching</div>
              </div>
              <a href="https://tidycal.com/jrobunitedgrp/discovery" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-medium text-[#0b1224] transition hover:scale-[1.02]">
                Start the Conversation <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* Footer */}
      <footer className="mt-16 border-t border-white/10 bg-[#060b19] py-10 pb-12">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-8 px-6 lg:flex-row md:px-10 lg:px-16">
          <div className="text-sm text-white/50 text-center lg:text-left">
            © {new Date().getFullYear()} Dr. Jeff Robinson. All rights reserved.
          </div>
          
          <nav className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-sm text-white/70">
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <a href="#brands" className="hover:text-white transition-colors">Brands</a>
            <a href="#speaking" className="hover:text-white transition-colors">Speaking</a>
            <a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a>
            <a href="#contact" className="hover:text-white transition-colors">Contact</a>
          </nav>

          <div className="flex items-center justify-center gap-6">
            <a href="https://www.facebook.com/JRobin65" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#1877F2] transition-colors" aria-label="Facebook">
              <SafeIcon icon={FiFacebook} className="h-5 w-5" />
            </a>
            <a href="https://www.instagram.com/drjeffspeaks" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#E4405F] transition-colors" aria-label="Instagram">
              <SafeIcon icon={FiInstagram} className="h-5 w-5" />
            </a>
            <a href="https://www.tiktok.com/@drjeffspeaks" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors" aria-label="TikTok">
              <SafeIcon icon={FaTiktok} className="h-5 w-5" />
            </a>
            <a href="https://www.linkedin.com/in/jeffrobinsonedd/" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#0A66C2] transition-colors" aria-label="LinkedIn">
              <SafeIcon icon={FiLinkedin} className="h-5 w-5" />
            </a>
          </div>
        </div>
      </footer>
    </main>
  );
}